import React, { useEffect } from 'react';
import Head from 'next/head';
import { appWithI18Next } from 'ni18n';
import { ToastContainer } from 'react-toastify';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AppCacheProvider } from '@mui/material-nextjs/v15-pagesRouter';
import { useStore } from 'react-redux';
import { FlagsProvider, RouteGuard, ErrorBoundary } from '@components';
import { LicenseInfo } from '@mui/x-license';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClient, QueryClientProvider } from 'react-query';

import { usePreviousPath } from 'hooks/common/usePreviousPath';
import '@silevis/reactgrid/styles.css';
import theme from '../config/theme';
import '../styles/globals.css';
import '../styles/proximaNova.css';
import '../styles/reactgrid.css';
import '../styles/ticketCreationNotification.css';
import 'react-toastify/dist/ReactToastify.css';

import { ni18nConfig } from '../ni18n.config';

import { wrapper } from '../store';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
  },
});

LicenseInfo.setLicenseKey(process.env.MUI_LICENSE_KEY);

function MyApp({ Component, pageProps }) {
  usePreviousPath();

  const [mounted, setMounted] = React.useState(false);
  const getLayout = Component.getLayout || ((page) => page);
  const defaults =
    typeof window !== 'undefined' &&
    JSON.parse(localStorage.getItem('firebaseFlags'));

  useEffect(() => {
    setMounted(true);
  }, []);
  const store = useStore();

  return (
    <AppCacheProvider>
      <PersistGate persistor={store.__persistor}>
        <QueryClientProvider client={queryClient}>
          <Head>
            <meta
              name="viewport"
              content="initial-scale=1, width=device-width"
            />
          </Head>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <div style={{ visibility: mounted ? 'visible' : 'hidden' }}>
              <ErrorBoundary
                showErrorDetails={process.env.NODE_ENV === 'development'}
              >
                <RouteGuard>
                  <FlagsProvider defaults={defaults || {}}>
                    {getLayout(<Component {...pageProps} />)}
                  </FlagsProvider>
                </RouteGuard>
              </ErrorBoundary>
              <ToastContainer />
            </div>
          </ThemeProvider>
        </QueryClientProvider>
      </PersistGate>
    </AppCacheProvider>
  );
}
export default appWithI18Next(wrapper.withRedux(MyApp), ni18nConfig);
